import { useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  useListPtoActsQuery,
  useRecordPtoExecutedMutation,
  useLazyExportPtoReportXlsxQuery,
  type PtoAct,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 420px) 1fr;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const LineRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 70px 90px 80px 28px;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

const ActRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2.5)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

interface LineDraft {
  name: string;
  unit: string;
  code: string;
  qty: string;
}

function emptyLine(): LineDraft {
  return { name: '', unit: '', code: '', qty: '' };
}

export function PtoReports() {
  const { projectId = '' } = useParams();
  const { can_write } = useAgentPermission('pto');
  const { data, isLoading } = useListPtoActsQuery(projectId, { skip: !projectId });
  const [record, { isLoading: saving }] = useRecordPtoExecutedMutation();
  const [exportXlsx, { isFetching: exporting }] = useLazyExportPtoReportXlsxQuery();

  const [period, setPeriod] = useState('');
  const [party, setParty] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [error, setError] = useState<string | null>(null);

  const acts: PtoAct[] = data?.items ?? [];

  function setLine(i: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const payload = lines
      .filter((l) => l.name.trim())
      .map((l) => ({
        name: l.name.trim(),
        unit: l.unit.trim() || undefined,
        code: l.code.trim() || undefined,
        qty_period: l.qty.trim() ? Number(l.qty.replace(',', '.')) : undefined,
      }));
    if (!period.trim() || payload.length === 0) {
      setError('Укажите период и хотя бы одну строку с наименованием.');
      return;
    }
    try {
      await record({
        projectId,
        period: period.trim(),
        party: party || undefined,
        lines: payload,
      }).unwrap();
      setLines([emptyLine()]);
    } catch {
      setError('Не удалось записать выполнение.');
    }
  }

  async function onExport() {
    try {
      const blob = await exportXlsx(projectId).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pto_report_${projectId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Не удалось выгрузить отчёт XLSX.');
    }
  }

  return (
    <div>
      <Row>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Отчётность о выполнении</h1>
          <Muted>Ввод выполнения по периодам · накопительная ведомость</Muted>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button $variant="primary" $size="sm" disabled={exporting} onClick={onExport}>
            {exporting ? 'Формирование…' : 'Скачать отчёт XLSX'}
          </Button>
          <Link to={`/agents/pto/${projectId}`}>
            <Button $variant="secondary">← К проекту</Button>
          </Link>
        </div>
      </Row>

      <Columns>
        <div>
          <h3 style={{ marginTop: 0 }}>Ввод выполнения</h3>
          {can_write ? (
            <Panel as="form" onSubmit={onSubmit}>
              <Field>
                Период (ГГГГ-ММ)
                <Input
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  placeholder="2026-06"
                />
              </Field>
              <Field>
                Сторона (для сверки ГП↔СП, опц.)
                <select
                  value={party}
                  onChange={(e) => setParty(e.target.value)}
                  style={{ padding: '8px 10px', borderRadius: 8 }}
                >
                  <option value="">— не указана —</option>
                  <option value="gp">Генподряд (ГП)</option>
                  <option value="sp">Субподряд (СП)</option>
                </select>
              </Field>
              <Muted style={{ marginBottom: 8 }}>Строки выполнения</Muted>
              {lines.map((l, i) => (
                <LineRow key={i}>
                  <Input
                    value={l.name}
                    onChange={(e) => setLine(i, { name: e.target.value })}
                    placeholder="Наименование"
                  />
                  <Input
                    value={l.unit}
                    onChange={(e) => setLine(i, { unit: e.target.value })}
                    placeholder="ед."
                  />
                  <Input
                    value={l.code}
                    onChange={(e) => setLine(i, { code: e.target.value })}
                    placeholder="шифр"
                  />
                  <Input
                    value={l.qty}
                    onChange={(e) => setLine(i, { qty: e.target.value })}
                    placeholder="кол-во"
                  />
                  <Button
                    type="button"
                    $variant="ghost"
                    $size="sm"
                    onClick={() =>
                      setLines((prev) =>
                        prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev,
                      )
                    }
                  >
                    ×
                  </Button>
                </LineRow>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Button
                  type="button"
                  $variant="ghost"
                  $size="sm"
                  onClick={() => setLines((prev) => [...prev, emptyLine()])}
                >
                  + строка
                </Button>
                <Button $variant="primary" type="submit" disabled={saving}>
                  {saving ? 'Сохранение…' : 'Записать выполнение'}
                </Button>
              </div>
              {error && <Muted style={{ color: 'crimson', marginTop: 8 }}>{error}</Muted>}
            </Panel>
          ) : (
            <Muted>Недостаточно прав для ввода выполнения.</Muted>
          )}
        </div>

        <div>
          <h3 style={{ marginTop: 0 }}>Периоды выполнения</h3>
          {isLoading ? (
            <Muted>Загрузка…</Muted>
          ) : acts.length === 0 ? (
            <Muted>Выполнение ещё не введено.</Muted>
          ) : (
            <Panel>
              {acts.map((a) => (
                <ActRow key={a.id}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{a.act_no ?? '—'}</div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>{a.source_filename}</div>
                  </div>
                  <Muted>{new Date(a.created_at).toLocaleDateString('ru-RU')}</Muted>
                </ActRow>
              ))}
            </Panel>
          )}
        </div>
      </Columns>
    </div>
  );
}
